import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  BasicCreatureInstance,
  CustomParticipant,
  Encounter, 
  // EncounterLog, 
  EncounterParticipant,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import InitiativeTrackerParticipant from "@dndComponents/encounter/InitiativeTrackerParticipant";
import CheckBox from "@components/input/CheckBox";
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';

interface Props { 
  obj: Encounter;
  change_time: number;
  onChange: (recalc: boolean) => void;
  onSelect: (selected_participant_id: string, selected_ci_id?: string) => void;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function InitiativeTracker({
  obj,
  change_time,
  onChange,
  onSelect,
}: Props) {
  const [prev_change_time, setChangeTime] = useState(change_time);
  const [reloading, setReloading] = useState(false);
  const [selected_participant_id, setSelectedParticipantId] = useState("");
  const [selected_ci_id, setSelectedCIId] = useState("");

  useEffect(() => {
    if (change_time !== prev_change_time) {
      setChangeTime(change_time);
    }
  }, [change_time, prev_change_time, setChangeTime]);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const updateEncounter = (onFinished: Function | null = null) => {
    onChange(true);
    if (onFinished) {
      onFinished();
    }
  } 

  const nextTurn = (participants: EncounterParticipant[], onFinished: Function | null = null) => {
    let current_turn_participant = participants.find(o => o.participant_id === obj.current_turn_participant_id);
    if (current_turn_participant && current_turn_participant.initiative) {
      const initiative = current_turn_participant.initiative;
      const add_time = current_turn_participant.add_time;
      // debugger;
      const later_participants = participants.filter(o => o.initiative !== null && (o.initiative < initiative || (o.initiative === initiative && o.add_time > add_time))).sort((a, b) => { 
        if (a.initiative === null || b.initiative === null) {
          return -1;
        } else if (a.initiative === b.initiative) {
          return +a.add_time > +b.add_time ? 1 : -1;
        } else {
          return +a.initiative < +b.initiative ? 1 : -1;
        }
      });
      if (later_participants.length > 0) {
        obj.current_turn_participant_id = later_participants[0].participant_id;
      } else {
        obj.current_turn_participant_id = participants[0].participant_id;
      }
      // const logs: EncounterLog[] = [];
      // const log = new EncounterLog();
      // log.field = "current_turn_participant_id";
      // log.value = obj.current_turn_participant_id;
      // logs.push(log);
      current_turn_participant = participants.filter(o => o.participant_id === obj.current_turn_participant_id)[0];
      updateEncounter(onFinished);
    } else {
      current_turn_participant = participants.find(o => 
        o.initiative !== null
      );
    }
  }
  
  const owner = api.user_id === obj.owner_id || (obj.campaign && obj.campaign.owner_id === api.user_id);
  let participants = owner ? obj.participants : obj.participants.filter(o => !o.hide);
  if (obj.running) {
    participants = participants.sort((a, b) => { 
      if (a.initiative === null || b.initiative === null) {
        if (a.initiative !== null) {
          return -1;
        } else if (b.initiative !== null) {
          return 1;
        } else {
          if (a.include_in_difficulty && !b.include_in_difficulty) {
            return -1;
          } else if (!a.include_in_difficulty && b.include_in_difficulty) {
            return 1;
          } else {
            return 0;
          }
        }
      } else if (a.initiative === b.initiative) {
        return +a.add_time > +b.add_time ? 1 : -1;
      } else {
        return +a.initiative < +b.initiative ? 1 : -1;
      }
    });
  }
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      { owner &&
        <div style={{ flex: "0 0 auto" }}>
          <Grid container spacing={0} direction="row">
            <Grid size={{ xs: obj.current_turn_participant_id === "" ? 6 : 3 }}>
              <DandDButtonBox 
                name={ obj.running ? "Pause" : "Start" }
                disabled={ participants.filter(o => o.initiative !== null).length === 0 }
                onClick={() => {
                  const new_obj = obj;
                  new_obj.running = !new_obj.running;
                  if (new_obj.running) {
                    if (new_obj.current_turn_participant_id === "" && participants.filter(o => o.initiative !== null).length > 0) {
                      new_obj.current_turn_participant_id = participants.filter(o => o.initiative !== null)[0].participant_id;
                    }
                  }
                  updateEncounter();
                  setReloading(true);
                }}
              />
            </Grid>
            { obj.current_turn_participant_id !== "" &&
              <Grid size={4}>
                <DandDButtonBox 
                  name="End"
                  onClick={() => {
                    const new_obj = obj;
                    new_obj.running = false;
                    new_obj.current_turn_participant_id = "";
                    new_obj.current_initiative = -10;
                    updateEncounter();
                    setReloading(true);
                  }}
                />
              </Grid>
            }
            <Grid size={6}>
              <DandDButtonBox 
                name="Next Turn"
                disabled={ !obj.running }
                onClick={() => {
                  nextTurn(participants);
                }}
              />
            </Grid>
            <Grid size={6}>
              <Grid container spacing={0} direction="row">
                <Grid size={{ xs: 12, lg: 6 }}>
                  <DandDButtonBox 
                    name="Roll Missing"
                    disabled={ obj.running || obj.participants.filter(o => !(o.participant instanceof Character) && o.initiative === null && o.include_in_difficulty).length === 0 }
                    onClick={() => {
                      const new_obj = obj;
                      // debugger;
                      for (const participant of new_obj.participants.filter(o => 
                        !(o.participant instanceof Character) && 
                        o.initiative === null && o.include_in_difficulty)) {
                        const adv = participant.initiative_adv;
                        let roll = common_util.get_random(20);
                        if (adv > 0) {
                          roll = Math.max(roll, common_util.get_random(20));
                        } else if (adv < 0) {
                          roll = Math.min(roll, common_util.get_random(20));
                        }
                        // console.log(roll);
                        participant.initiative = Math.max(0, roll + participant.initiative_modifier);
                      }
                      // const log = new EncounterLog();
                      // log.field = "participants";
                      // log.value = "refresh";
                      updateEncounter();
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <DandDButtonBox 
                    name="Use Passives"
                    disabled={ obj.running || obj.participants.filter(o => !(o.participant instanceof Character) && o.initiative === null && o.include_in_difficulty).length === 0 }
                    onClick={() => {
                      const new_obj = obj;
                      // debugger;
                      for (const participant of new_obj.participants.filter(o => 
                        !(o.participant instanceof Character) && 
                        o.initiative === null && o.include_in_difficulty)) {
                        participant.initiative = 10 + participant.initiative_modifier;
                      }
                      // const log = new EncounterLog();
                      // log.field = "participants";
                      // log.value = "refresh";
                      updateEncounter();
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={6}>
              <Grid container spacing={0} direction="row">
                <Grid size={{ xs: 12, lg: 6 }}>
                  <DandDButtonBox 
                    name="Alternating Initiatives"
                    disabled={ obj.running || common_util.unique_array(obj.participants.filter(o => o.initiative !== null && o.participant_type !== "Lair Actions").map(o => o.color)).length < 2 }
                    onClick={() => {
                      const new_obj = obj;
                      const the_participants = new_obj.participants.filter(o => o.initiative !== null && o.participant_type !== "Lair Actions");
                      const colors = common_util.unique_array(the_participants.map(o => o.color));
                      const participants_by_color: { 
                        color: string, 
                        participants: {
                          participant: EncounterParticipant, 
                          new_init_mod: number
                        }[]
                      }[] = colors.map(color => {
                        return { color, participants: the_participants.filter(o => o.color === color).map(participant => {
                          return {
                            participant, new_init_mod: -1
                          };
                        }) };
                      });
                      const color_order = colors.map(color => {
                        return { color, highest: Math.max(...the_participants.filter(o2 => o2.color === color && o2.initiative !== null).map(o2 => {
                          if (o2.initiative === null) {
                            return -1;
                          } else {
                            return o2.initiative;
                          }
                        }))};
                      }).sort((a, b) => {
                        return +a.highest > +b.highest ? -1 : 1;
                      }).map(o => o.color);
                      let color_index = 0;
                      for (let i = 0; i < the_participants.length; ++i) {
                        let color = color_order[color_index];
                        if (!color) {
                          color_index = 0;
                          color = color_order[color_index];
                        }
                        if (color) {
                          let next_participant = participants_by_color.filter(o => 
                            o.color === color)[0].participants.filter(o => o.new_init_mod === null)[0];
                          while (!next_participant) {
                            color_index++;
                            color = color_order[color_index];
                            if (!color) {
                              color_index = 0;
                              color = color_order[color_index];
                            }
                              
                            next_participant = participants_by_color.filter(o => 
                              o.color === color)[0].participants.filter(o => o.new_init_mod === null)[0];
                          }
                          if (next_participant) {
                            next_participant.new_init_mod = i;
                          }
                        }
                        color_index++;
                      }
                      const max = the_participants.length > 25 ? the_participants.length : 25;
                      for (const participant of participants_by_color) {
                        for (const part of participant.participants) {
                          part.participant.initiative = max - part.new_init_mod;
                        }
                      }
                      // const log = new EncounterLog();
                      // log.field = "participants";
                      // log.value = "refresh";
                      updateEncounter();
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <DandDButtonBox 
                    name="Clear Initiatives"
                    disabled={ obj.running || obj.current_turn_participant_id !== "" || obj.participants.filter(o => o.initiative !== null && o.participant_type !== "Lair Actions").length === 0 }
                    onClick={() => {
                      const new_obj = obj;
                      const the_participants = new_obj.participants.filter(o => o.initiative !== null && o.participant_type !== "Lair Actions");
                      for (const part of the_participants) {
                        part.initiative = null;
                      }
                      // const log = new EncounterLog();
                      // log.field = "participants";
                      // log.value = "refresh";
                      updateEncounter();
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
      }
      <div className="padded bordered"
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column"
        }}
      >
        <div style={{ flex: "1 1 auto", minHeight: 0, overflowY: "scroll" }}>
          <Grid container spacing={0} direction="row">
            <Grid size={12}>
              <Grid container spacing={0} direction="row">
                { !reloading && participants.map(participant => {
                  return (
                    <Grid size={12} key={participant.participant_id}>
                      <InitiativeTrackerParticipant
                        obj={participant}
                        encounter={obj}
                        change_time={change_time}
                        selected_participant_id={selected_participant_id}
                        selected_ci_id={selected_ci_id}
                        current_turn_participant_id={obj.current_turn_participant_id}
                        onSelect={(ci: BasicCreatureInstance | CustomParticipant | null) => {
                          if (ci) {
                            const new_id = (participant.participant_id === selected_participant_id && ci.true_id === selected_ci_id ? "" : participant.participant_id);
                            setSelectedParticipantId(new_id);
                            setSelectedCIId(new_id === "" ? "" : ci.true_id);
                            onSelect(new_id, new_id === "" ? "" : ci.true_id);
                          } else {
                            const new_id = (participant.participant_id === selected_participant_id ? "" : participant.participant_id);
                            setSelectedParticipantId(new_id);
                            onSelect(new_id);
                          }
                        }}
                        onChange={() => {
                          updateEncounter();
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
}
